import React, { useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/cursos.css";
import CursosSummary from "../components/CursosSummary";
import CursosFilters from "../components/CursosFilters";
import CursosTable from "../components/CursosTable.";
import CursosFormModal from "../components/CursosModalForm";
import CoordinacionTabs from "../../catalogo/components/CoordinacionTabs";
import { usePermissions } from "../../../../utils/permissions";
import { isModuloHabilitado } from "../../../../utils/modulosSistema";

export default function CursosView({
    cursos,
    cursosPaginados,
    upsertCurso,
    resumen,
    filtros,
    setFiltros,
    quickFilter,
    activeQuickFilter,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    totalPages,
    total,
    goToPage,
    loading,
    error,
    centros,
    niveles,
    especialidades,
    centrosLoading,
    nivelesLoading,
    centrosError,
    nivelesError,
    especialidadesError,
    loadingCatalogos,
    reload,
    reloadCentros,
    reloadNiveles,
    reloadEspecialidades,
    form,
}) {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission("cursos.crear");
    const canUpdate = hasPermission("cursos.actualizar");
    const canDelete = hasPermission("cursos.eliminar");
    const showSync  = isModuloHabilitado("integracion_contifico");

    useEffect(() => {
        if (typeof document === "undefined") return undefined;
        if (form.modal) document.body.classList.add("modal-open");
        else document.body.classList.remove("modal-open");
        return () => document.body.classList.remove("modal-open");
    }, [form.modal]);

    const lastSync = lastUpdated ? lastUpdated.toLocaleString() : "Sincronizacion inicial";
    const catalogError = centrosError || nivelesError || especialidadesError;

    // Solo cursos "raiz" (que no son ya variantes de otro) pueden ser el curso oficial de una variante.
    const cursosOficialesOptions = (cursos || []).filter((c) => !(c.cursoOficialId ?? c.curso_oficial_id));

    return (
        <section className="management-page management-theme-blue cursos-wrapper">
            <CoordinacionTabs />
            <div className="management-card management-header mb-4">
                <div>
                    <p className="management-eyebrow mb-1">Academico / Cursos</p>
                    <h4 className="management-title mb-1 d-flex align-items-center gap-2">
                        <i className="bi bi-mortarboard" />
                        Gestion de cursos
                    </h4>
                    <p className="text-muted mb-0">Administra el catalogo, asignaciones y precios base.</p>
                </div>
                <div className="d-flex flex-column align-items-end gap-2">
                    {canCreate && (
                        <button type="button" className="btn management-btn-primary" onClick={form.abrirCrear}>
                            <i className="bi bi-plus-circle me-2" />
                            Nuevo curso
                        </button>
                    )}
                    <small className="text-muted">Actualizado: {lastSync}</small>
                </div>
            </div>

            <CursosSummary resumen={resumen} onQuickFilter={quickFilter} activeKey={activeQuickFilter} />

            <CursosFilters
                filtros={filtros}
                setFiltros={setFiltros}
                limpiarFiltros={limpiarFiltros}
                centros={centros}
                niveles={niveles}
                centrosLoading={centrosLoading}
                nivelesLoading={nivelesLoading}
            />

            {catalogError && (
                <div className="management-card management-alert d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div>
                        {centrosError && <p className="mb-1">Centros: {centrosError}</p>}
                        {nivelesError && <p className="mb-1">Niveles: {nivelesError}</p>}
                        {especialidadesError && <p className="mb-0">Especialidades: {especialidadesError}</p>}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        {centrosError && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reloadCentros}>
                                Reintentar centros
                            </button>
                        )}
                        {nivelesError && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reloadNiveles}>
                                Reintentar niveles
                            </button>
                        )}
                        {especialidadesError && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reloadEspecialidades}>
                                Reintentar especialidades
                            </button>
                        )}
                    </div>
                </div>
            )}

            <CursosTable
                cursos={cursosPaginados ?? cursos}
                loading={loading}
                error={error}
                reload={reload}
                onEdit={canUpdate ? form.abrirEditar : undefined}
                onDelete={canDelete ? form.abrirEliminar : undefined}
                canEdit={canUpdate}
                canDelete={canDelete}
                showSync={showSync}
                onSynced={(curso, res) => upsertCurso?.({
                    ...curso,
                    contificoProductoId: res.contifico_producto_id,
                })}
            />

            <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="text-muted small">
                    Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(total, page * pageSize)} de {total}
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="btn-group">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                            <i className="bi bi-chevron-left" />
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" disabled>
                            Pagina {page} / {totalPages}
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => goToPage(page + 1)}
                            disabled={page >= totalPages}
                        >
                            <i className="bi bi-chevron-right" />
                        </button>
                    </div>
                </div>
            </div>

            <CursosFormModal
                {...form}
                centros={centros}
                niveles={niveles}
                especialidades={especialidades}
                cursosOficialesOptions={cursosOficialesOptions}
                loadingOptions={loadingCatalogos}
                canCreate={canCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
            />
        </section>
    );
}
